import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, LogOut, Clock, MapPin, Building2, CheckCircle2 } from "lucide-react";
import {
    referrerRequestAccess,
    referrerMe,
    referrerPatients,
    formatApiError,
} from "@/lib/api";
import { DOMAIN_LABEL, SLOT_LABEL, LOCATION_LABEL } from "@/lib/content";

const TOKEN_KEY = "ana_referrer_token";

const STATUS_META = {
    pending: { label: "En attente de confirmation", color: "#d89b92" },
    confirmed: { label: "Confirmé", color: "#6b5548" },
    rejected: { label: "Refusé", color: "#a63333" },
    done: { label: "Séance faite", color: "#5a6560" },
};

function formatDateFr(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("fr-CH", {
            weekday: "long", day: "numeric", month: "long", year: "numeric",
        });
    } catch {
        return iso;
    }
}

export default function Referrer() {
    const navigate = useNavigate();
    const location = useLocation();
    const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

    // Ingest ?token=xxx from magic link, persist, then clean URL
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const t = params.get("token");
        if (t) {
            localStorage.setItem(TOKEN_KEY, t);
            setToken(t);
            navigate("/medecin", { replace: true });
        }
    }, [location.search, navigate]);

    const logout = () => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
    };

    if (!token) return <ReferrerRequestForm />;
    return <ReferrerDashboard onLogout={logout} />;
}

/* ------------------------------------------------------------------ */
/*  Request access — magic link                                        */
/* ------------------------------------------------------------------ */
function ReferrerRequestForm() {
    const [email, setEmail] = useState("");
    const [website, setWebsite] = useState(""); // honeypot
    const [sent, setSent] = useState(false);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            await referrerRequestAccess(email);
            if (!website) setSent(true);
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    return (
        <main data-testid="referrer-request" style={{ paddingTop: 160, minHeight: "100vh" }}>
            <section style={{ padding: "40px 40px 120px" }}>
                <div className="mx-auto" style={{ maxWidth: 620 }}>
                    <div className="font-mono-clinical" style={{ color: "var(--terra)" }}>Espace médecin référent</div>
                    <div className="divider-terra mt-3" />
                    <h1 className="font-display mt-8" style={{ fontSize: "clamp(2.4rem, 5vw, 3.6rem)", lineHeight: 1, fontWeight: 400, color: "var(--forest-dark)" }}>
                        Suivez vos <span style={{ fontStyle: "italic", color: "var(--terra)" }}>patients adressés</span>.
                    </h1>
                    <p style={{ fontSize: 15, color: "var(--ink-mid)", marginTop: 20, lineHeight: 1.65, maxWidth: 540 }}>
                        Consultez le statut de chaque prise en charge : demande reçue, rendez-vous confirmé, séance faite.
                        Aucun mot de passe — un lien d'accès sécurisé est envoyé à l'adresse professionnelle
                        que vous avez enregistrée depuis le formulaire de rendez-vous.
                    </p>

                    {sent ? (
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            data-testid="referrer-sent-panel"
                            style={{ marginTop: 40, background: "var(--cream-warm)", border: "1px solid var(--hairline)", padding: 32, borderRadius: 4 }}
                        >
                            <div className="flex items-center gap-3">
                                <CheckCircle2 size={20} style={{ color: "var(--forest-dark)" }} />
                                <div className="font-display" style={{ fontSize: 22, color: "var(--forest-dark)" }}>
                                    Vérifiez votre <span style={{ fontStyle: "italic", color: "var(--terra)" }}>boîte mail</span>.
                                </div>
                            </div>
                            <p style={{ fontSize: 14, color: "var(--ink-mid)", marginTop: 14, lineHeight: 1.65 }}>
                                Si l'adresse <strong>{email}</strong> est bien enregistrée dans notre annuaire,
                                un lien sécurisé vient de vous être envoyé. Il reste actif 30 jours.
                            </p>
                            <p style={{ fontSize: 13, color: "var(--ink-soft)", marginTop: 12, fontStyle: "italic" }}>
                                Pas d'email reçu ? Adressez-nous un patient depuis le formulaire de rendez-vous
                                (en cochant « je suis médecin ») pour vous enregistrer, puis réessayez.
                            </p>
                        </motion.div>
                    ) : (
                        <form
                            onSubmit={submit}
                            data-testid="referrer-request-form"
                            style={{ marginTop: 40, background: "var(--cream)", border: "1px solid var(--hairline)", padding: 32, borderRadius: 4 }}
                        >
                            <label className="field-label">Email professionnel</label>
                            <input
                                required
                                type="email"
                                data-testid="referrer-email"
                                className="field-clinical"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="dr.dupont@cabinet.ch"
                            />

                            {/* Honeypot */}
                            <input
                                type="text"
                                tabIndex={-1}
                                aria-hidden="true"
                                autoComplete="off"
                                value={website}
                                onChange={(e) => setWebsite(e.target.value)}
                                style={{ position: "absolute", left: -9999, width: 1, height: 1, opacity: 0 }}
                            />

                            {error && <div style={{ marginTop: 16, color: "#a63333", fontSize: 13 }}>{error}</div>}

                            <button
                                type="submit"
                                disabled={loading}
                                data-testid="referrer-request-submit"
                                className="btn-primary mt-8"
                                style={{ opacity: loading ? 0.6 : 1 }}
                            >
                                <Mail size={14} /> {loading ? "Envoi…" : "Recevoir mon lien d'accès"}
                            </button>

                            <p style={{ fontSize: 12, color: "var(--ink-soft)", marginTop: 20, lineHeight: 1.6 }}>
                                Pas encore enregistré ? Utilisez le bouton
                                <Link to="/rendez-vous" className="link-underline" style={{ margin: "0 4px" }}>
                                    « je suis médecin »
                                </Link>
                                lors de votre prochaine demande de rendez-vous — votre cabinet
                                sera ajouté à l'annuaire.
                            </p>
                        </form>
                    )}
                </div>
            </section>
        </main>
    );
}

/* ------------------------------------------------------------------ */
/*  Dashboard — patient list                                           */
/* ------------------------------------------------------------------ */
function ReferrerDashboard({ onLogout }) {
    const [me, setMe] = useState(null);
    const [patients, setPatients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const [profile, list] = await Promise.all([
                    referrerMe(),
                    referrerPatients(),
                ]);
                setMe(profile);
                setPatients(list);
            } catch (e) {
                setError(formatApiError(e));
                // Expired / invalid → force logout so the form is shown
                if (e?.response?.status === 401) {
                    localStorage.removeItem(TOKEN_KEY);
                    setTimeout(() => window.location.reload(), 1200);
                }
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    if (loading) {
        return (
            <main style={{ paddingTop: 160, minHeight: "100vh" }}>
                <section style={{ padding: "40px 40px 120px" }}>
                    <div className="mx-auto" style={{ maxWidth: 1200, color: "var(--ink-soft)" }}>
                        Chargement de votre espace…
                    </div>
                </section>
            </main>
        );
    }

    if (error) {
        return (
            <main style={{ paddingTop: 160, minHeight: "100vh" }}>
                <section style={{ padding: "40px 40px 120px" }}>
                    <div className="mx-auto" style={{ maxWidth: 620 }}>
                        <div style={{ color: "#a63333", fontSize: 14 }}>{error}</div>
                        <p style={{ marginTop: 12, fontSize: 13, color: "var(--ink-soft)" }}>
                            Session expirée — vous allez être redirigé vers la page de connexion.
                        </p>
                    </div>
                </section>
            </main>
        );
    }

    const counts = patients.reduce((acc, p) => {
        acc[p.status] = (acc[p.status] || 0) + 1;
        return acc;
    }, {});

    return (
        <main data-testid="referrer-dashboard" style={{ paddingTop: 160, minHeight: "100vh" }}>
            <section style={{ padding: "40px 40px 40px" }}>
                <div className="mx-auto" style={{ maxWidth: 1200 }}>
                    <div className="flex items-start justify-between flex-wrap gap-4">
                        <div>
                            <div className="font-mono-clinical" style={{ color: "var(--terra)" }}>
                                Espace médecin référent
                            </div>
                            <div className="divider-terra mt-3" />
                            <h1 className="font-display mt-6" style={{ fontSize: "clamp(2.4rem, 5vw, 3.6rem)", lineHeight: 1, fontWeight: 400, color: "var(--forest-dark)" }}>
                                Bonjour, <span style={{ fontStyle: "italic", color: "var(--terra)" }}>{me?.name || "Docteur"}</span>.
                            </h1>
                            {me?.cabinet && (
                                <div className="mt-3 flex items-center gap-2" style={{ fontSize: 14, color: "var(--ink-mid)" }}>
                                    <Building2 size={14} style={{ color: "var(--terra)" }} />
                                    {me.cabinet}
                                </div>
                            )}
                        </div>
                        <button onClick={onLogout} className="btn-ghost" data-testid="referrer-logout">
                            <LogOut size={14} /> Déconnexion
                        </button>
                    </div>

                    <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[
                            { key: "all", label: "Adressés", value: patients.length, color: "var(--forest-dark)" },
                            { key: "pending", label: STATUS_META.pending.label, value: counts.pending || 0, color: STATUS_META.pending.color },
                            { key: "confirmed", label: STATUS_META.confirmed.label, value: counts.confirmed || 0, color: STATUS_META.confirmed.color },
                            { key: "done", label: STATUS_META.done.label, value: counts.done || 0, color: STATUS_META.done.color },
                        ].map((s) => (
                            <div
                                key={s.key}
                                data-testid={`referrer-stat-${s.key}`}
                                style={{ background: "var(--cream)", border: "1px solid var(--hairline)", padding: 20, borderRadius: 4 }}
                            >
                                <div style={{ fontSize: 11, color: s.color, textTransform: "uppercase", letterSpacing: "0.12em" }}>
                                    {s.label}
                                </div>
                                <div className="font-display mt-2" style={{ fontSize: 34, color: "var(--forest-dark)", lineHeight: 1, fontStyle: "italic" }}>
                                    {s.value}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-14">
                        <div className="flex items-baseline justify-between mb-5">
                            <div className="font-mono-clinical" style={{ color: "var(--terra)" }}>Historique des demandes</div>
                            <div style={{ fontSize: 12, color: "var(--ink-soft)", fontStyle: "italic" }}>
                                Identités patients masquées — RGPD
                            </div>
                        </div>

                        <div className="grid grid-cols-1 gap-3">
                            {patients.map((p) => (
                                <motion.div
                                    key={p.id}
                                    initial={{ opacity: 0, y: 6 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.35 }}
                                    data-testid={`referrer-patient-${p.id}`}
                                    className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center"
                                    style={{ background: "var(--cream)", border: "1px solid var(--hairline)", padding: 20, borderRadius: 4 }}
                                >
                                    <div className="md:col-span-3">
                                        <div className="flex items-center gap-3">
                                            <span style={{
                                                width: 40, height: 40, borderRadius: "50%",
                                                background: "var(--cream-warm)",
                                                border: "1px solid var(--hairline)",
                                                display: "inline-flex", alignItems: "center", justifyContent: "center",
                                                fontFamily: "var(--font-display)", fontStyle: "italic",
                                                color: "var(--forest-dark)", fontSize: 15,
                                            }}>
                                                {p.initials}
                                            </span>
                                            <div>
                                                <div className="font-display" style={{ fontSize: 18, color: "var(--forest-dark)", fontWeight: 500, lineHeight: 1 }}>
                                                    Patient {p.initials}
                                                </div>
                                                <div style={{ fontSize: 11, color: "var(--ink-soft)", marginTop: 4 }}>
                                                    Demandé le {new Date(p.created_at).toLocaleDateString("fr-CH", { day: "numeric", month: "short", year: "numeric" })}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="md:col-span-4" style={{ fontSize: 13, color: "var(--ink-mid)" }}>
                                        <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.12em", color: "var(--forest)" }}>
                                            {DOMAIN_LABEL[p.domain] || p.domain}
                                        </div>
                                        <div className="mt-2 flex items-center gap-2">
                                            <Clock size={12} style={{ color: "var(--terra)" }} />
                                            {formatDateFr(p.preferred_date)} · {SLOT_LABEL[p.preferred_slot] || p.preferred_slot}
                                        </div>
                                        <div className="mt-1 flex items-center gap-2" style={{ color: p.location === "domicile" ? "var(--terra)" : "var(--ink-mid)" }}>
                                            <MapPin size={12} />
                                            {LOCATION_LABEL[p.location] || "En cabinet"}
                                        </div>
                                    </div>
                                    <div className="md:col-span-5 text-right">
                                        <span
                                            data-testid={`referrer-patient-status-${p.id}`}
                                            style={{
                                                display: "inline-flex", alignItems: "center", gap: 8,
                                                padding: "8px 14px",
                                                borderRadius: 999,
                                                border: `1px solid ${STATUS_META[p.status]?.color || "var(--hairline)"}`,
                                                color: STATUS_META[p.status]?.color || "var(--ink-mid)",
                                                fontSize: 12, letterSpacing: "0.06em", textTransform: "uppercase",
                                                background: "transparent",
                                            }}
                                        >
                                            <span style={{ width: 6, height: 6, borderRadius: "50%", background: STATUS_META[p.status]?.color }} />
                                            {STATUS_META[p.status]?.label || p.status}
                                        </span>
                                    </div>
                                </motion.div>
                            ))}
                            {patients.length === 0 && (
                                <div style={{ textAlign: "center", padding: 60, color: "var(--ink-soft)", fontSize: 14 }}>
                                    Aucun patient adressé pour l'instant.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
}
